import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { fetchNotifyTeamDefault } from '@/api/teamChat';

type Props = {
  projectId?: string;
  notifyTeam: boolean;
  onNotifyTeamChange: (value: boolean) => void;
  notifyMessage: string;
  onNotifyMessageChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
};

export function NotifyTeamFields({
  projectId,
  notifyTeam,
  onNotifyTeamChange,
  notifyMessage,
  onNotifyMessageChange,
  disabled = false,
  className,
}: Props) {
  const { t } = useTranslation('common');
  const [visible, setVisible] = useState(false);
  const [defaultsLoaded, setDefaultsLoaded] = useState(false);

  useEffect(() => {
    if (!projectId) {
      setVisible(false);
      return;
    }
    let cancelled = false;
    void fetchNotifyTeamDefault(projectId).then((res) => {
      if (cancelled) return;
      setVisible(res.teammateCount > 1);
      if (!defaultsLoaded && res.defaultChecked) {
        onNotifyTeamChange(true);
      }
      setDefaultsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, defaultsLoaded, onNotifyTeamChange]);

  if (!visible || !projectId) return null;

  return (
    <div className={clsx('space-y-2 rounded-lg border border-border bg-muted/10 px-3 py-3', className)}>
      <label className="flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          checked={notifyTeam}
          disabled={disabled}
          onChange={(e) => onNotifyTeamChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
        />
        <span className="text-sm">
          <span className="font-semibold text-foreground">{t('teamChat.notifyTeam.label')}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground leading-relaxed">
            {t('teamChat.notifyTeam.hint')}
          </span>
        </span>
      </label>
      {notifyTeam ? (
        <textarea
          value={notifyMessage}
          disabled={disabled}
          onChange={(e) => onNotifyMessageChange(e.target.value)}
          rows={2}
          placeholder={t('teamChat.notifyTeam.messagePlaceholder')}
          className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      ) : null}
    </div>
  );
}

export function useNotifyTeamState(initial = false) {
  const [notifyTeam, setNotifyTeam] = useState(initial);
  const [notifyMessage, setNotifyMessage] = useState('');
  const reset = useCallback(() => {
    setNotifyTeam(false);
    setNotifyMessage('');
  }, []);
  return { notifyTeam, setNotifyTeam, notifyMessage, setNotifyMessage, reset };
}
